import { FieldArray } from 'formik'
import { map, get, omit, isEmpty } from 'lodash'
import React from 'react'
import { DATA_TEST_CLASS } from 'shared-constants/build/testIds'
import { ShippingMethodList } from '@public/components/ShippingMethodList'
import { getProductQuantityFieldName } from '@public/Showroom/productQuantityUtils'
import { Callout, CALLOUT_INTENT, CALLOUT_VARIANT } from 'components/Callout'
import { Card, CARD_PADDING_SIZE } from 'components/Card'
import { CHECKOUT_FIELD_NAMES } from 'components/Checkout/constants'
import Icon, { ICON_SIZE } from 'components/Icon'
import { Box, Flex } from 'components/Layout'
import { Loader, LOADER_SIZE } from 'components/Loader'
import { Text } from 'components/Typography'
import { type CartContextItem } from 'providers/cart'
import { useGoogleAnalytics } from 'providers/googleAnalytics'
import { findDiscount } from 'routes/utils/findDiscount'
import { COLOR_INTENT, FONT_SIZE, FONT_WEIGHT, SPACE } from 'Theme'
import { type CalculatePriceCheckoutQuery } from 'types/graphql-generated'
import { ProductListItem } from './ProductListItem'
import { onRemoveItemTracking, onSetItemTracking } from './utils'

interface ProductListCheckoutProps {
  shippingMethods:
    | Record<
        string,
        {
          id: number
          method: string
          shippedAtUtc: string
          shippingCost?: {
            total: number
            currencyIsoCode: string
          }
        }[]
      >
    | Record<string, never>
  onRemoveCartItem: (
    sourceIdAndMethodType: string,
    productVariantId: number
  ) => Promise<void>
  onSetCartItem: (
    sourceIdAndMethodType: string,
    productVariantId: number,
    quantity: number
  ) => Promise<void>
  isCartShipmentLoading?: boolean
  cartShipmentsLoadingSources: string[]
  cartItemsBySourceAndMethodType: Record<string, CartContextItem[]>
  discountItems?: CalculatePriceCheckoutQuery['calculatePriceCheckout']['discountItems']
}

export const ProductListCheckout: React.FC<
  React.PWC<ProductListCheckoutProps>
> = ({
  cartItemsBySourceAndMethodType,
  onRemoveCartItem,
  onSetCartItem,
  shippingMethods,
  isCartShipmentLoading,
  cartShipmentsLoadingSources,
  discountItems,
}) => {
  const { executeDataToDataLayer } = useGoogleAnalytics()

  return (
    <Flex flexDirection="column" width="100%">
      <FieldArray
        name={CHECKOUT_FIELD_NAMES.SHIPPING_METHOD}
        render={({ form: { setFieldValue, values } }) =>
          map(
            cartItemsBySourceAndMethodType,
            (items, sourceIdAndMethodType) => {
              const isLoading =
                isCartShipmentLoading &&
                (!cartShipmentsLoadingSources.length ||
                  cartShipmentsLoadingSources.some(
                    (value) => value === sourceIdAndMethodType
                  ))

              return (
                <Card
                  key={sourceIdAndMethodType}
                  hasBorders
                  paddingSize={CARD_PADDING_SIZE.NONE}
                  data-test-class={DATA_TEST_CLASS.CART_LIST_ITEM}
                  mb={SPACE.PX_10}
                >
                  <Box width="100%">
                    {items.map((cartItem) => {
                      const {
                        id: cartItemId,
                        cartQuantity,
                        productVariant: {
                          id,
                          quantity,
                          managedCustomFields,
                          ...restProductVariant
                        },
                        ...rest
                      } = cartItem
                      const discount = findDiscount({ id, discountItems })
                      return (
                        <ProductListItem
                          key={`${sourceIdAndMethodType}-${id}`}
                          isCheckout
                          id={id}
                          discount={discount}
                          onRemove={() => {
                            setFieldValue(
                              CHECKOUT_FIELD_NAMES.SHIPPING_METHOD,
                              omit(
                                values[CHECKOUT_FIELD_NAMES.SHIPPING_METHOD],
                                sourceIdAndMethodType
                              )
                            )
                            onRemoveCartItem(sourceIdAndMethodType, cartItemId)
                            onRemoveItemTracking({
                              executeDataToDataLayer,
                              cartItem,
                              cartQuantity,
                            })
                          }}
                          onSetCartItem={(quantityValue) => {
                            onSetCartItem(
                              sourceIdAndMethodType,
                              id,
                              quantityValue
                            )
                            onSetItemTracking({
                              executeDataToDataLayer,
                              cartQuantity,
                              cartItem,
                              quantityToSet: quantityValue,
                            })
                          }}
                          cartQuantity={cartQuantity}
                          isLoading={isCartShipmentLoading}
                          quantity={quantity}
                          quantityFieldName={`${
                            CHECKOUT_FIELD_NAMES.ITEMS_QUANTITY
                          }.${getProductQuantityFieldName(id)}`}
                          managedCustomFields={managedCustomFields}
                          {...rest}
                          {...restProductVariant}
                        />
                      )
                    })}
                    {sourceIdAndMethodType &&
                      shippingMethods &&
                      shippingMethods[sourceIdAndMethodType] && (
                        <ShippingMethodList
                          sourceIdAndMethodType={sourceIdAndMethodType}
                          shippingMethods={
                            shippingMethods[sourceIdAndMethodType]
                          }
                        />
                      )}
                    {isLoading && (
                      <Callout
                        intent={CALLOUT_INTENT.INFO}
                        variant={CALLOUT_VARIANT.ROUNDED_BOTTOM}
                        icon={() => <Loader size={LOADER_SIZE.XXS} />}
                        alignItems="center"
                        py={SPACE.PX_10}
                      >
                        <Text
                          fontSize={FONT_SIZE.PX_13}
                          fontWeight={FONT_WEIGHT.MEDIUM}
                        >
                          Shipping methods are loading...
                        </Text>
                      </Callout>
                    )}
                    {!isLoading &&
                      !get(shippingMethods, sourceIdAndMethodType) && (
                        <Callout
                          intent={CALLOUT_INTENT.INFO}
                          variant={CALLOUT_VARIANT.ROUNDED_BOTTOM}
                          icon={() => (
                            <Icon.Info
                              size={ICON_SIZE.PX_20}
                              color={COLOR_INTENT.GRAY_70}
                            />
                          )}
                          alignItems="center"
                          py={SPACE.PX_10}
                        >
                          <Text
                            fontSize={FONT_SIZE.PX_13}
                            fontWeight={FONT_WEIGHT.MEDIUM}
                          >
                            {isEmpty(shippingMethods)
                              ? `We're unable to calculate your shipping at this time. Don't worry - you can go ahead and reserve the item(s) in your cart now. Our team will then be in touch with a shipping quote and a link to complete the payment!`
                              : `We're unable to calculate shipping at this time. Don't worry - you can go ahead and reserve this item and move forward with purchasing the other item(s) in your cart. Our team will then be in touch with a shipping quote and a link to complete payment for any outstanding items!`}
                          </Text>
                        </Callout>
                      )}
                  </Box>
                </Card>
              )
            }
          )
        }
      />
    </Flex>
  )
}
